import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  LogOut, Plus, Trash2, Building2, ChevronDown, ChevronRight, Loader2, AlertTriangle
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuildingStructure() {
  const { lang } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [towers, setTowers] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [selectedTower, setSelectedTower] = useState('kirei1');
  const [expandedFloor, setExpandedFloor] = useState(null);
  const [showAddUnit, setShowAddUnit] = useState(null); // floor number
  const [addingFloor, setAddingFloor] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // {type, id, floor}

  const fetchData = useCallback(async () => {
    try {
      const [towersRes, aptsRes] = await Promise.all([
        axios.get(`${API}/towers`),
        axios.get(`${API}/apartments?tower=${selectedTower}`),
      ]);
      setTowers(towersRes.data);
      setApartments(aptsRes.data);
    } catch {}
  }, [selectedTower]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const currentTower = towers.find(t => t.id === selectedTower);
  const totalFloors = currentTower?.total_floors || 0;

  const getFloorApts = (floor) => apartments.filter(a => a.floor === floor);

  const handleAddFloor = async () => {
    setAddingFloor(true);
    try {
      await axios.put(`${API}/towers/${selectedTower}/floors`);
      toast.success(lang === 'es' ? `Piso ${totalFloors + 1} agregado` : `Floor ${totalFloors + 1} added`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setAddingFloor(false);
    }
  };

  const handleRemoveFloor = async (floor) => {
    try {
      await axios.delete(`${API}/towers/${selectedTower}/floors/${floor}`);
      toast.success(lang === 'es' ? `Piso ${floor} eliminado` : `Floor ${floor} removed`);
      setConfirmDelete(null);
      setExpandedFloor(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteApt = async (aptId) => {
    try {
      await axios.delete(`${API}/apartments/${aptId}`);
      toast.success(lang === 'es' ? `Unidad ${aptId} eliminada` : `Unit ${aptId} deleted`);
      setConfirmDelete(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  if (!user) return null;

  return (
    <div data-testid="building-structure-page" className="min-h-screen bg-[#F5F5F7] pt-24 pb-16">
      <Toaster position="top-right" richColors />
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1
              className="text-3xl md:text-4xl font-light tracking-tight mb-1"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A1A1A' }}
              data-testid="structure-title"
            >
              {lang === 'es' ? 'Estructura del Edificio' : 'Building Structure'}
            </h1>
            <p className="text-sm text-[#8A828F]">{user?.name} — {user?.role}</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              data-testid="structure-to-dashboard"
              className="flex items-center gap-2 text-[#8A828F] hover:text-[#D4AF37] transition-colors text-sm tracking-[0.1em] uppercase"
            >
              <Building2 size={16} />
              Dashboard
            </button>
            <button
              onClick={async () => { await logout(); navigate('/admin'); }}
              data-testid="structure-logout"
              className="flex items-center gap-2 text-[#8A828F] hover:text-[#C62828] transition-colors text-sm tracking-[0.1em] uppercase"
            >
              <LogOut size={16} />
              {lang === 'es' ? 'Salir' : 'Logout'}
            </button>
          </div>
        </div>

        {/* Tower selector */}
        <div className="flex gap-3 mb-6">
          {towers.map(tw => (
            <button
              key={tw.id}
              data-testid={`structure-tower-${tw.id}`}
              onClick={() => { setSelectedTower(tw.id); setExpandedFloor(null); setShowAddUnit(null); }}
              className={`px-6 py-3 text-sm tracking-[0.1em] uppercase border transition-all ${
                selectedTower === tw.id
                  ? 'bg-[#D4AF37] text-white border-[#D4AF37]'
                  : 'bg-white text-[#8A828F] border-[#E5E5E5] hover:border-[#D4AF37]'
              }`}
            >
              {tw.name} — {tw.total_floors} {lang === 'es' ? 'pisos' : 'floors'}, {tw.total_units} {lang === 'es' ? 'uds' : 'units'}
            </button>
          ))}
        </div>

        {/* Floors list */}
        <div className="space-y-2">
          {Array.from({ length: totalFloors }, (_, i) => totalFloors - i).map(floor => {
            const floorApts = getFloorApts(floor);
            const isExpanded = expandedFloor === floor;

            return (
              <div key={floor} className="bg-white border border-[#E5E5E5] overflow-hidden" data-testid={`structure-floor-${floor}`}>
                {/* Floor header */}
                <button
                  onClick={() => setExpandedFloor(isExpanded ? null : floor)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-[#FAFAFA] transition-colors"
                  data-testid={`expand-floor-${floor}`}
                >
                  <div className="flex items-center gap-4">
                    {isExpanded ? <ChevronDown size={16} className="text-[#D4AF37]" /> : <ChevronRight size={16} className="text-[#8A828F]" />}
                    <span
                      className="text-lg font-light"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: isExpanded ? '#D4AF37' : '#1A1A1A' }}
                    >
                      {floor >= 9 ? 'PH — ' : ''}{lang === 'es' ? 'Piso' : 'Floor'} {floor}
                    </span>
                    <span className="text-xs text-[#8A828F] tracking-[0.08em]">
                      {floorApts.length} {floorApts.length === 1 ? (lang === 'es' ? 'unidad' : 'unit') : (lang === 'es' ? 'unidades' : 'units')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Status dots summary */}
                    <div className="flex gap-1">
                      {floorApts.map(a => (
                        <div
                          key={a.id}
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: a.status === 'available' ? '#2E7D32' : a.status === 'reserved' ? '#D4AF37' : '#C62828' }}
                        />
                      ))}
                    </div>
                  </div>
                </button>

                {/* Expanded content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-[#E5E5E5] px-6 py-4">
                        {/* Units list */}
                        {floorApts.length > 0 ? (
                          <div className="space-y-2 mb-4">
                            {floorApts.map(apt => (
                              <div
                                key={apt.id}
                                className="flex items-center justify-between px-4 py-3 bg-[#F5F5F7] border border-[#E5E5E5]"
                                data-testid={`structure-unit-${apt.id}`}
                              >
                                <div className="flex items-center gap-4">
                                  <span className="text-sm font-medium text-[#1A1A1A] w-20">{apt.unit}</span>
                                  <span className="text-xs text-[#8A828F]">{apt.area_m2} m²</span>
                                  <span className="text-xs text-[#8A828F]">${apt.price.toLocaleString()}</span>
                                  <span className="text-xs text-[#8A828F]">{apt.bedrooms}H / {apt.bathrooms}B</span>
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: apt.status === 'available' ? '#2E7D32' : apt.status === 'reserved' ? '#D4AF37' : '#C62828' }}
                                  />
                                </div>
                                {/* Delete unit */}
                                {confirmDelete?.type === 'unit' && confirmDelete?.id === apt.id ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-red-600">{lang === 'es' ? '¿Eliminar?' : 'Delete?'}</span>
                                    <button
                                      onClick={() => handleDeleteApt(apt.id)}
                                      data-testid={`confirm-delete-unit-${apt.id}`}
                                      className="text-[10px] bg-red-600 text-white px-3 py-1 uppercase tracking-wider"
                                    >
                                      {lang === 'es' ? 'Sí' : 'Yes'}
                                    </button>
                                    <button
                                      onClick={() => setConfirmDelete(null)}
                                      className="text-[10px] border border-[#E5E5E5] text-[#8A828F] px-3 py-1 uppercase tracking-wider"
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setConfirmDelete({ type: 'unit', id: apt.id })}
                                    data-testid={`delete-unit-${apt.id}`}
                                    className="text-[#C5C0C8] hover:text-red-600 transition-colors p-1"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[#8A828F] mb-4 py-2">
                            {lang === 'es' ? 'Sin unidades en este piso' : 'No units on this floor'}
                          </p>
                        )}

                        {/* Add unit form */}
                        {showAddUnit === floor ? (
                          <AddUnitForm
                            tower={selectedTower}
                            floor={floor}
                            lang={lang}
                            onDone={() => { setShowAddUnit(null); fetchData(); }}
                            onCancel={() => setShowAddUnit(null)}
                          />
                        ) : (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setShowAddUnit(floor)}
                              data-testid={`add-unit-btn-${floor}`}
                              className="flex items-center gap-2 text-xs tracking-[0.1em] uppercase border border-[#D4AF37] text-[#D4AF37] px-4 py-2 hover:bg-[#D4AF37] hover:text-white transition-all"
                            >
                              <Plus size={14} />
                              {lang === 'es' ? 'Agregar Unidad' : 'Add Unit'}
                            </button>

                            {/* Remove floor */}
                            {confirmDelete?.type === 'floor' && confirmDelete?.floor === floor ? (
                              <div className="flex items-center gap-2 ml-auto">
                                <AlertTriangle size={14} className="text-red-600" />
                                <span className="text-xs text-red-600">
                                  {lang === 'es'
                                    ? `¿Eliminar piso ${floor} y sus ${floorApts.length} unidades?`
                                    : `Delete floor ${floor} and its ${floorApts.length} units?`}
                                </span>
                                <button
                                  onClick={() => handleRemoveFloor(floor)}
                                  data-testid={`confirm-delete-floor-${floor}`}
                                  className="text-[10px] bg-red-600 text-white px-3 py-1 uppercase tracking-wider"
                                >
                                  {lang === 'es' ? 'Sí, eliminar' : 'Yes, delete'}
                                </button>
                                <button
                                  onClick={() => setConfirmDelete(null)}
                                  className="text-[10px] border border-[#E5E5E5] text-[#8A828F] px-3 py-1 uppercase tracking-wider"
                                >
                                  {lang === 'es' ? 'Cancelar' : 'Cancel'}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmDelete({ type: 'floor', floor })}
                                data-testid={`delete-floor-${floor}`}
                                className="flex items-center gap-1 ml-auto text-xs text-[#C5C0C8] hover:text-red-600 transition-colors"
                              >
                                <Trash2 size={12} />
                                {lang === 'es' ? 'Eliminar Piso' : 'Remove Floor'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Add floor button */}
        <button
          onClick={handleAddFloor}
          disabled={addingFloor}
          data-testid="add-floor-btn"
          className="mt-4 w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#D4AF37]/40 text-[#D4AF37] py-4 uppercase tracking-[0.1em] text-sm font-light hover:bg-[#D4AF37]/5 hover:border-[#D4AF37] transition-all disabled:opacity-50"
        >
          {addingFloor ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          {lang === 'es' ? `Agregar Piso ${totalFloors + 1}` : `Add Floor ${totalFloors + 1}`}
        </button>
      </div>
    </div>
  );
}

/* ============= ADD UNIT FORM ============= */
function AddUnitForm({ tower, floor, lang, onDone, onCancel }) {
  const [form, setForm] = useState({
    unit: '',
    price: '',
    area_m2: '',
    bedrooms: '2',
    bathrooms: '2',
    has_balcony: false,
    orientation: '',
    status: 'available',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.unit || !form.price || !form.area_m2 || !form.bedrooms || !form.bathrooms) {
      toast.error(lang === 'es' ? 'Complete todos los campos requeridos' : 'Fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/apartments/create`, {
        tower,
        floor,
        unit: form.unit,
        price: parseFloat(form.price),
        area_m2: parseFloat(form.area_m2),
        bedrooms: parseInt(form.bedrooms),
        bathrooms: parseInt(form.bathrooms),
        has_balcony: form.has_balcony,
        orientation: form.orientation,
        status: form.status,
      });
      toast.success(lang === 'es' ? `Unidad ${form.unit} creada` : `Unit ${form.unit} created`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5"
      data-testid={`add-unit-form-${floor}`}
    >
      <p className="text-xs tracking-[0.1em] uppercase text-[#D4AF37] mb-4 font-medium">
        {lang === 'es' ? `Nueva Unidad — Piso ${floor}` : `New Unit — Floor ${floor}`}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="text-[10px] uppercase text-[#8A828F] mb-1 block">{lang === 'es' ? 'Nombre/ID *' : 'Name/ID *'}</Label>
          <Input
            value={form.unit}
            onChange={e => setForm(p => ({ ...p, unit: e.target.value }))}
            placeholder="4008"
            className="h-9 rounded-none border-[#E5E5E5] text-sm"
            data-testid="new-unit-name"
            required
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8A828F] mb-1 block">{lang === 'es' ? 'Precio (USD) *' : 'Price (USD) *'}</Label>
          <Input
            type="number"
            value={form.price}
            onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
            placeholder="350000"
            className="h-9 rounded-none border-[#E5E5E5] text-sm"
            data-testid="new-unit-price"
            required
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8A828F] mb-1 block">{lang === 'es' ? 'Área (m²) *' : 'Area (m²) *'}</Label>
          <Input
            type="number"
            value={form.area_m2}
            onChange={e => setForm(p => ({ ...p, area_m2: e.target.value }))}
            placeholder="120"
            className="h-9 rounded-none border-[#E5E5E5] text-sm"
            data-testid="new-unit-area"
            required
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8A828F] mb-1 block">{lang === 'es' ? 'Habitaciones *' : 'Bedrooms *'}</Label>
          <Input
            type="number"
            value={form.bedrooms}
            onChange={e => setForm(p => ({ ...p, bedrooms: e.target.value }))}
            className="h-9 rounded-none border-[#E5E5E5] text-sm"
            data-testid="new-unit-beds"
            required
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8A828F] mb-1 block">{lang === 'es' ? 'Baños *' : 'Bathrooms *'}</Label>
          <Input
            type="number"
            step="0.5"
            min="0"
            value={form.bathrooms}
            onChange={e => setForm(p => ({ ...p, bathrooms: e.target.value }))}
            className="h-9 rounded-none border-[#E5E5E5] text-sm"
            data-testid="new-unit-baths"
            required
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8A828F] mb-1 block">{lang === 'es' ? 'Orientación' : 'Orientation'}</Label>
          <Input
            value={form.orientation}
            onChange={e => setForm(p => ({ ...p, orientation: e.target.value }))}
            placeholder="Norte"
            className="h-9 rounded-none border-[#E5E5E5] text-sm"
            data-testid="new-unit-orientation"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8A828F] mb-1 block">{lang === 'es' ? 'Estado' : 'Status'}</Label>
          <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
            <SelectTrigger className="h-9 rounded-none border-[#E5E5E5] text-sm" data-testid="new-unit-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="available">{lang === 'es' ? 'Disponible' : 'Available'}</SelectItem>
              <SelectItem value="reserved">{lang === 'es' ? 'Reservado' : 'Reserved'}</SelectItem>
              <SelectItem value="sold">{lang === 'es' ? 'Vendido' : 'Sold'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer h-9">
            <input
              type="checkbox"
              checked={form.has_balcony}
              onChange={e => setForm(p => ({ ...p, has_balcony: e.target.checked }))}
              className="w-4 h-4 accent-[#D4AF37]"
              data-testid="new-unit-balcony"
            />
            <span className="text-xs text-[#8A828F]">{lang === 'es' ? 'Balcón' : 'Balcony'}</span>
          </label>
        </div>
      </div>
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={saving}
          data-testid="submit-new-unit"
          className="flex items-center gap-2 bg-[#D4AF37] text-white px-6 py-2.5 text-xs tracking-[0.1em] uppercase hover:bg-[#B5952F] transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? (lang === 'es' ? 'Guardando...' : 'Saving...') : (lang === 'es' ? 'Crear Unidad' : 'Create Unit')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs border border-[#E5E5E5] text-[#8A828F] px-6 py-2.5 tracking-[0.1em] uppercase hover:text-[#1A1A1A] transition-colors"
        >
          {lang === 'es' ? 'Cancelar' : 'Cancel'}
        </button>
      </div>
    </motion.form>
  );
}
